import { memo, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useNotify, useTranslate, useLocale } from 'react-admin';

import { paths } from 'src/routes/paths';

import {
  Box,
  Card,
  Chip,
  Paper,
  Stack,
  Alert,
  Switch,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import { DashboardContent } from 'src/layouts/dashboard';
import { Iconify } from 'src/components/iconify';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';

import dayjs from 'dayjs';

import { KPI_RANGE_DEFAULT, useWorldCupOverview } from './hooks/useWorldCupOverview';

const KPI_MIN_DATE = '2026-06-11';
const KPI_MAX_DATE = '2026-07-19';

const isoToDateInput = (iso?: string) => {
  if (!iso) return '';
  const parsed = dayjs.utc(iso);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const dateInputToFromIso = (date: string) => {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
};

const dateInputToToIso = (date: string) => {
  if (!date) return '';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 0)).toISOString();
};
import type {
  EventSettings,
  OverviewFocusMatch,
  OverviewHealthRow,
  OverviewKpi,
  UpdateSwitchesPayload,
} from './types';
import {
  formatInt,
  formatSignedInt,
  formatSignedUsd,
  formatTime,
  getHealthStatusKey,
  getMatchStatusKey,
  getTeamName,
} from './utils';

const I18N_PREFIX = 'module.worldCup.overview';

const HEALTH_SERVICE_I18N_KEY: Record<string, string> = {
  match_feed: 'health.services.matchFeed',
  config: 'health.services.config',
  settlement: 'health.services.settlement',
  treasury: 'health.services.treasury',
};

type TranslateFn = ReturnType<typeof useTranslate>;

type SwitchTarget = keyof Pick<EventSettings, 'maintenanceMode'>;

type KpiCardItem = {
  title: string;
  value: string;
  desc?: string;
  color: string;
};

type KpiCardProps = KpiCardItem;

type FocusNowPanelProps = {
  current: OverviewFocusMatch | null;
  upcoming: OverviewFocusMatch[];
};

type MatchRowProps = {
  match: OverviewFocusMatch;
  active?: boolean;
};

type HealthPanelProps = {
  rows: OverviewHealthRow[];
};

type HealthRowProps = {
  row: OverviewHealthRow;
};

type SwitchPanelProps = {
  settings: EventSettings;
  disabled?: boolean;
  readonly?: boolean;
  onChange: (field: SwitchTarget, checked: boolean) => void;
};

type SwitchItemProps = {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};

type SwitchReasonDialogProps = {
  open: boolean;
  reason: string;
  loading?: boolean;
  onReasonChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
};

const tOverview = (t: TranslateFn, key: string, options?: Record<string, unknown>) =>
  t(`${I18N_PREFIX}.${key}`, options);

export const MainPage = () => {
  const t = useTranslate();
  const notify = useNotify();

  const {
    kpi,
    focusNow,
    health,
    switches,
    kpiRange,
    loading,
    refreshing,
    kpiLoading,
    switching,
    refetch,
    setKpiRange,
    updateSwitches,
  } = useWorldCupOverview();

  const [switchTarget, setSwitchTarget] = useState<{
    field: SwitchTarget;
    checked: boolean;
  } | null>(null);
  const [reason, setReason] = useState('');

  const kpiCards = useMemo(() => buildKpiCards(kpi, t), [kpi, t]);

  const handleRefresh = useCallback(async () => {
    await refetch();
    notify(tOverview(t, 'notify.refreshSuccess'), { type: 'success' });
  }, [notify, refetch, t]);

  const openSwitchDialog = useCallback(
    (field: SwitchTarget, checked: boolean) => {
      const reasonKey = getSwitchReasonKey(field, checked);

      setSwitchTarget({ field, checked });
      setReason(tOverview(t, reasonKey));
    },
    [t]
  );

  const closeSwitchDialog = useCallback(() => {
    if (switching) return;

    setSwitchTarget(null);
    setReason('');
  }, [switching]);

  const confirmSwitchChange = useCallback(async () => {
    if (!switchTarget) return;

    const finalReason = reason.trim();

    if (!finalReason) {
      notify(tOverview(t, 'notify.reasonRequired'), { type: 'warning' });
      return;
    }

    try {
      const payload: UpdateSwitchesPayload = {
        reason: finalReason,
        maintenanceMode: switchTarget.checked,
      };

      await updateSwitches(payload);

      notify(tOverview(t, 'notify.switchSuccess'), { type: 'success' });
      setSwitchTarget(null);
      setReason('');
    } catch {
      notify(tOverview(t, 'notify.switchFailed'), { type: 'error' });
    }
  }, [notify, reason, switchTarget, t, updateSwitches]);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={tOverview(t, 'title')}
        links={[{ name: t('resource.worldCup') }, { name: t('resource.worldCupOverview') }]}
        action={
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="date"
              label={tOverview(t, 'kpi.from')}
              InputLabelProps={{ shrink: true }}
              value={isoToDateInput(kpiRange.from)}
              onChange={(event) =>
                setKpiRange((prev) => ({
                  ...prev,
                  from: dateInputToFromIso(event.target.value) || KPI_RANGE_DEFAULT.from,
                }))
              }
              inputProps={{
                min: KPI_MIN_DATE,
                max: isoToDateInput(kpiRange.to) || KPI_MAX_DATE,
              }}
            />
            <TextField
              size="small"
              type="date"
              label={tOverview(t, 'kpi.to')}
              InputLabelProps={{ shrink: true }}
              value={isoToDateInput(kpiRange.to)}
              onChange={(event) =>
                setKpiRange((prev) => ({
                  ...prev,
                  to: dateInputToToIso(event.target.value) || KPI_RANGE_DEFAULT.to,
                }))
              }
              inputProps={{
                min: isoToDateInput(kpiRange.from) || KPI_MIN_DATE,
                max: KPI_MAX_DATE,
              }}
            />
            <Button
              size="medium"
              variant="contained"
              disabled={
                kpiRange.from === KPI_RANGE_DEFAULT.from && kpiRange.to === KPI_RANGE_DEFAULT.to
              }
              onClick={() => setKpiRange({ ...KPI_RANGE_DEFAULT })}
            >
              {tOverview(t, 'kpi.reset')}
            </Button>
            <Button
              size="medium"
              variant="contained"
              disabled={refreshing}
              onClick={handleRefresh}
              startIcon={<Iconify icon="eva:refresh-fill" />}
            >
              {refreshing ? tOverview(t, 'actions.refreshing') : tOverview(t, 'actions.refresh')}
            </Button>
          </Stack>
        }
        sx={{ mb: 3 }}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tOverview(t, 'description')}
      </Typography>

      {loading && !kpi ? (
        <CenterBox>
          <CircularProgress size={28} />
        </CenterBox>
      ) : (
        <Stack spacing={3}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  xl: 'repeat(4, 1fr)',
                },
                gap: 2,
              }}
            >
              {kpiCards.map((item) => (
                <KpiCard key={item.title} {...item} />
              ))}
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' },
              gap: 3,
            }}
          >
            <FocusNowPanel
              current={focusNow?.current ?? null}
              upcoming={focusNow?.upcoming ?? []}
            />
            <HealthPanel rows={health} />
          </Box>

          <SwitchPanel settings={switches} disabled={switching} onChange={openSwitchDialog} />
        </Stack>
      )}

      <SwitchReasonDialog
        open={Boolean(switchTarget)}
        reason={reason}
        loading={switching}
        onReasonChange={setReason}
        onCancel={closeSwitchDialog}
        onConfirm={confirmSwitchChange}
      />
    </DashboardContent>
  );
};

function buildKpiCards(kpi: OverviewKpi | null, t: TranslateFn): KpiCardItem[] {
  return [
    {
      title: tOverview(t, 'kpi.totalParticipants'),
      value: kpi ? formatInt(kpi.totalParticipants) : '--',
      desc: kpi
        ? formatSignedInt(kpi.participantsDelta24h, ` ${tOverview(t, 'kpi.last24h')}`)
        : '--',
      color: 'divider.dark',
    },
    {
      title: tOverview(t, 'kpi.totalVouchersIssued'),
      value: kpi ? formatInt(+kpi.vouchersIssued + +kpi.totalManualVoucher) : '--',
      color: 'primary.main',
    },
    {
      title: tOverview(t, 'kpi.perpetualsVouchersIssued'),
      value: kpi ? formatInt(+kpi.voucherPerpIssued) : '--',
      color: 'info.dark',
    },
    {
      title: tOverview(t, 'kpi.predictionVouchersIssued'),
      value: kpi ? formatInt(+kpi.voucherPredictionIssued) : '--',
      color: 'info.main',
    },
    {
      title: tOverview(t, 'kpi.leverageVouchersIssued'),
      value: kpi ? formatInt(+kpi.voucherLeverageIssued) : '--',
      color: 'warning.dark',
    },
    {
      title: tOverview(t, 'kpi.manualVouchersIssue'),
      value: kpi ? formatInt(+kpi.totalManualVoucher) : '--',
      color: 'warning.main',
    },
    {
      title: tOverview(t, 'kpi.focusPool'),
      value: kpi ? formatInt(kpi.focusPoolUsdc) : '--',
      desc: kpi
        ? tOverview(t, 'kpi.focusPoolDelta', {
            value: formatSignedUsd(kpi.focusPoolDelta24h, ` ${tOverview(t, 'kpi.today')}`),
          })
        : '--',
      color: 'success.main',
    },
    {
      title: tOverview(t, 'kpi.pendingSettle'),
      value: kpi ? formatInt(kpi.pendingSettleCount) : '--',
      desc: tOverview(t, 'kpi.pendingSettleDesc'),
      color: 'divider',
    },
  ];
}

function getSwitchReasonKey(field: SwitchTarget, checked: boolean) {
  return checked ? 'switchReason.enableMaintenance' : 'switchReason.disableMaintenance';
}

function getHealthServiceLabelKey(service: string) {
  return HEALTH_SERVICE_I18N_KEY[service] ?? service;
}

function getHealthThemeColor(status?: string) {
  switch (status) {
    case 'healthy':
      return 'success.main';
    case 'degraded':
      return 'warning.main';
    case 'down':
      return 'error.main';
    default:
      return 'text.disabled';
  }
}

const KpiCard = memo((props: KpiCardProps) => (
  <Card sx={{ p: 3, borderLeft: 4, borderColor: props.color }}>
    <Typography variant="body2" color="text.secondary">
      {props.title}
    </Typography>

    <Typography variant="h4" fontWeight={700} sx={{ mt: 1 }}>
      {props.value}
    </Typography>
    {props.desc && (
      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
        {props.desc}
      </Typography>
    )}
  </Card>
));

KpiCard.displayName = 'KpiCard';

const FocusNowPanel = memo(({ current, upcoming }: FocusNowPanelProps) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const rows = current ? [current, ...upcoming.slice(0, 4)] : upcoming.slice(0, 4);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">{tOverview(t, 'focus.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {tOverview(t, 'focus.desc')}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(paths.worldCup.focusMatchConfig.root)}
        >
          {tOverview(t, 'focus.goConfig')}
        </Button>
      </Stack>

      <Stack spacing={1}>
        {rows.length ? (
          rows.map((match, index) => (
            <MatchRow
              key={`${match.scheduleId}-${match.matchId}`}
              match={match}
              active={index === 0}
            />
          ))
        ) : (
          <EmptyText text={tOverview(t, 'focus.empty')} />
        )}
      </Stack>
    </Card>
  );
});

FocusNowPanel.displayName = 'FocusNowPanel';

const MatchRow = memo(({ match, active }: MatchRowProps) => {
  const t = useTranslate();
  const locale = useLocale();
  const isActive = Boolean(active);
  const isLive = isActive && match.status === 'live';

  const mapStage = (match: OverviewFocusMatch) => {
    const stage = match.stage || '';
    if (stage === 'group') {
      return t('module.worldCup.focusMatch.stage.group', { group: match.groupName || '--' });
    }
    return t(`module.worldCup.focusMatch.stage.${stage}`) || stage || '--';
  };

  return (
    <Paper
      variant={isActive ? 'elevation' : 'outlined'}
      elevation={isActive ? 4 : 0}
      sx={{
        p: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '120px 1fr auto auto' },
        alignItems: 'center',
        gap: 1.5,
        ...(isActive && {
          borderLeft: 4,
          borderColor: 'primary.main',
        }),
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {mapStage(match)}
      </Typography>

      <Typography variant="body2" fontWeight={700}>
        {match.homeTeam?.flagEmoji ? `${match.homeTeam.flagEmoji} ` : ''}
        {getTeamName(match.homeTeam, locale)}{' '}
        <Typography variant="caption" color="text.secondary">
          {match.homeTeam?.code}
        </Typography>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {`  VS  `}
        </Typography>
        {match.awayTeam?.flagEmoji ? `${match.awayTeam.flagEmoji} ` : ''}
        {getTeamName(match.awayTeam, locale)}{' '}
        <Typography variant="caption" color="text.secondary">
          {match.awayTeam?.code}
        </Typography>
      </Typography>

      <Typography variant="body2" color="text.secondary">
        {match.status === 'live'
          ? tOverview(t, 'match.liveMinute', { minute: match.liveMinute ?? '--' })
          : formatTime(match.kickoffAt)}
      </Typography>

      <Chip
        label={
          isLive
            ? `LIVE ${match.homeScore ?? '-'} - ${match.awayScore ?? '-'}`
            : tOverview(t, `match.status.${getMatchStatusKey(match.status)}`)
        }
        color={isLive ? 'success' : 'info'}
        size="small"
      />
    </Paper>
  );
});

MatchRow.displayName = 'MatchRow';

const HealthPanel = memo(({ rows }: HealthPanelProps) => {
  const t = useTranslate();

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">{tOverview(t, 'health.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {tOverview(t, 'health.desc')}
      </Typography>

      <Stack spacing={1} sx={{ mt: 2.5 }}>
        {rows.length ? (
          rows.map((row) => <HealthRow key={row.service} row={row} />)
        ) : (
          <EmptyText text={tOverview(t, 'health.empty')} />
        )}
      </Stack>
    </Card>
  );
});

HealthPanel.displayName = 'HealthPanel';

const HealthRow = memo(({ row }: HealthRowProps) => {
  const t = useTranslate();
  const color = getHealthThemeColor(row.status);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
    >
      <Stack direction="row" spacing={1.2} alignItems="center">
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: color,
          }}
        />

        <Typography variant="body2" fontWeight={700}>
          {tOverview(t, getHealthServiceLabelKey(row.service))}
        </Typography>
      </Stack>

      <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
        {tOverview(t, `health.status.${getHealthStatusKey(row.status)}`)} · {row.detail}
      </Typography>
    </Paper>
  );
});

HealthRow.displayName = 'HealthRow';

const SwitchPanel = memo(({ settings, disabled, readonly, onChange }: SwitchPanelProps) => {
  const t = useTranslate();

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">{tOverview(t, 'switches.title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {tOverview(t, 'switches.desc')}
      </Typography>

      {readonly ? (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
          {tOverview(t, 'switches.noPermission')}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mt: 2.5,
        }}
      >
        <SwitchItem
          title={tOverview(t, 'switches.maintenanceMode')}
          desc={tOverview(t, 'switches.maintenanceModeDesc')}
          checked={settings.maintenanceMode}
          disabled={disabled}
          onChange={(checked) => onChange('maintenanceMode', checked)}
        />
      </Box>
    </Card>
  );
});

SwitchPanel.displayName = 'SwitchPanel';

const SwitchItem = memo(({ title, desc, checked, disabled, onChange }: SwitchItemProps) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      <Box>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {desc}
        </Typography>
      </Box>

      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, nextChecked) => onChange(nextChecked)}
      />
    </Stack>
  </Paper>
));

SwitchItem.displayName = 'SwitchItem';

const SwitchReasonDialog = memo(
  ({ open, reason, loading, onReasonChange, onCancel, onConfirm }: SwitchReasonDialogProps) => {
    const t = useTranslate();

    return (
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>{tOverview(t, 'switchDialog.title')}</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {tOverview(t, 'switchDialog.desc')}
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            value={reason}
            disabled={loading}
            onChange={(event) => onReasonChange(event.target.value)}
            label={tOverview(t, 'switchDialog.reason')}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>

          <Button variant="contained" onClick={onConfirm} disabled={loading}>
            {loading ? t('common.saving') : t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

SwitchReasonDialog.displayName = 'SwitchReasonDialog';

const EmptyText = memo(({ text }: { text: string }) => (
  <Stack alignItems="center" sx={{ py: 4 }}>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Stack>
));

EmptyText.displayName = 'EmptyText';

const CenterBox = ({ children }: { children: ReactNode }) => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
    {children}
  </Stack>
);
